import React, { useRef, useEffect, useState } from "react";
import "./NurseList.css";
import { useTranslation } from "react-i18next";
import Widgets from "../../../../widgets";
import Icons from "../../../../Assets/Icons";
import { scrollToTop } from "../../../../modules/scrollToTop/scrollAction";
import { getAllNurse } from "../../../../modules/NursignCare/ManageNurse/ManageNurseThunk";
import strings from "../../../../utils/strings";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

const NurseList = () => {
  const { t } = useTranslation();
  const breadcrumbs = ["nursing_care", "manage_nurse"];
  const paths = ["/manage-resource/nurse-list", "/manage-resource/nurse-list"];
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const statuses = queryParams.getAll("status");
  const [showFilter, setShowFilter] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState(statuses);
  const modalRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1); // Default to page 1
  const itemsPerPage = 10; // Define how many items per page you want to show
  const dispatch = useDispatch();
  const statusTypes = ["Active", "Inactive", "Pending"];
  const [totalPages, setTotalPages] = useState(1); // Set the number of pages based on total data and items per page
  const [nurses, setNurses] = useState([]);
  const topRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNurses = async () => {
      try {
        const data = await dispatch(
          getAllNurse({
            page: currentPage,
            limit: itemsPerPage,
            search: "",
          }),
        ).unwrap();

        // API response handling
        if (data?.status === "success") {
          setNurses(data?.data?.nurses || []);
          setTotalPages(data?.data?.totalPages || 1);
        } else {
          setNurses([]); // Reset if no valid data is found
          setTotalPages(0); // No pages if no nurses are found
        }
      } catch (error) {
        console.error("Failed to fetch nurses:", error.message);
      }
    };

    fetchNurses();
  }, [dispatch, currentPage]);

  const scrollToTop = () => {
    if (topRef.current) {
      topRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };
  useEffect(() => {
    scrollToTop();
  }, []);
  const handleSearch = async (searchTerm) => {
    try {
      const data = await dispatch(
        getAllNurse({
          page: 1,
          limit: itemsPerPage,
          search: searchTerm,
        }),
      ).unwrap();

      // API response handling
      if (data?.status === "success") {
        setNurses(data?.data?.nurses || []);
        setTotalPages(data?.data?.totalPages || 1);
        setCurrentPage(1);
      } else {
        setNurses([]);
        setTotalPages(0);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Search failed:", error.message);
    }
  };

  const handleStatusChange = (newStatuses) => {
    setSelectedStatuses(newStatuses);
    console.log("Selected statuses:", newStatuses);
  };

  const handleClick = (pageNumber) => {
    dispatch(scrollToTop());
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalRef]);

  const handleVerifyClick = (nurseId) => {
    console.log("Nurse clicked, ID:", nurseId);
    // Navigate to the next page with the nurseID in the URL
    navigate(`/manage-resource/get-nurse/${nurseId}`);
  };

  const statusIcons = {
    ACTIVE: Icons.Check,
    INACTIVE: Icons.CircleCross,
  };

  const handlePrevNext = (direction) => {
    dispatch(scrollToTop());
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) {
        pages.push("...");
      }
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push("...");
      }
      pages.push(totalPages);
    }
    return pages;
  };
  const addNurseButton = () => {
    navigate(strings.navigation.createNurse);
  };

  return (
    <div className="nurse-container" ref={topRef}>
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="department-heading">{t("Nurse")}</div>
      <div className="nurse-search-line">
        <Widgets.SearchBox onSearch={handleSearch} placeholder={t("searchbynursename")} />
        <div className="nurse-add-filter">
          <Widgets.IconButton iconSrc={Icons.plusIconGreen} onClick={addNurseButton} buttonText={t("addNurseProfile")} />
          <div className="ppl-icon-container" ref={modalRef}>
            <section className="ppl-icon-filter" onClick={() => setShowFilter(!showFilter)}>
              <img src={Icons.filterIcon} alt="icon-filter" />
            </section>
            {showFilter && (
              <section className="ppl-status-dropdown">
                <Widgets.StatusDropdown
                  statusTypes={statusTypes}
                  onStatusChange={handleStatusChange}
                  setShowFilter={setShowFilter}
                  selectedStatuses={selectedStatuses}
                  setSelectedStatuses={setSelectedStatuses}
                />
              </section>
            )}
          </div>
        </div>
      </div>
      <div className="nurse-list-container">
        <div className="nurse-table-body">
          <table className="nurse-table">
            <thead className="nurse-thead">
              <tr>
                <th>{t("nurseDetails")}</th>
                <th>{t("employeeId")}</th>
                <th>{t("serviceGroup")}</th>
                <th>{t("status")}</th>
              </tr>
            </thead>
            <tbody className="ppl-tbody">
              {nurses.length > 0 ? (
                nurses.map((item, index) => (
                  <tr key={index} className="ppl-table-row" onClick={() => handleVerifyClick(item._id)}>
                    <td>
                      <div className="ppl-td-first">
                        <div>
                          {item.profilePhoto ? (
                            <img src={item.profilePhoto} alt="profile-pic" className="ppl-td-profile" />
                          ) : (
                            <div className="ppl-detials">
                              <img src={Icons.profile} alt="profile-pic" className="ppl-td-profile-none" />
                            </div>
                          )}
                        </div>
                        <div className="ppl-name-email">
                          <section>
                            {item.firstName} {item.lastName}
                          </section>
                          <section className="ppl-email">{item.gender}</section>
                        </div>
                      </div>
                    </td>
                    <td>{item.employeeID}</td>
                    <td>{item.specialization.join(", ")}</td>
                    {/* <td className={`ppl-status ${item.isActive ? "ACTIVE" : "INACTIVE"}`}>
                      <p>
                        <img src={statusIcons[item.isActive ? "ACTIVE" : "INACTIVE"]} alt="status" />
                        {item.isActive ? t("Active") : t("Inactive")}
                      </p>
                    </td> */}
                    <td className={`ppl-status ${item.isActive ? "active-status" : "inactive-status"}`}>
                      <p>
                        <img src={statusIcons[item.isActive ? "ACTIVE" : "INACTIVE"]} alt="status" />
                        {item.isActive ? t("active") : t("inactive")}
                      </p>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">
                    <div className="no-record-container ppl">
                      <div className="no-record-icon">
                        <img src={Icons.NoData} alt="No Data Found" />
                      </div>
                      <div className="no-record-text">{t("noRecordFound")}</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="nurse-pagination-list">
            {totalPages > 0 && (
              <>
                <button onClick={() => handlePrevNext("prev")} disabled={currentPage === 1} className="nurse-pagination-prev-list">
                  <img src={Icons.leftArrow} alt="<" />
                </button>

                {renderPagination().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === "number" && handleClick(page)}
                    disabled={page === currentPage || page === "..."}
                    className={`nurse-pagination-btn-list ${page === currentPage ? "active-list" : ""} ${page === "..." ? "ellipse" : ""}`}
                  >
                    {typeof page === "number" ? page : <img src={Icons.extraIcon} alt="..." className="nurse-extra-icon" />}
                  </button>
                ))}

                <button onClick={() => handlePrevNext("next")} disabled={currentPage === totalPages} className="nurse-pagination-next-list">
                  <img src={Icons.rightArrow} alt=">" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NurseList;
